import { useQuery } from "@tanstack/react-query";
import { api } from "../../../../singleton/axios";

export interface BalanceSheetStatement {
  date: string;
  symbol: string;
  reportedCurrency: string;
  cik: string;
  fillingDate: string;
  acceptedDate: string;
  calendarYear: string;
  period: string;
  cashAndCashEquivalents: number;
  shortTermInvestments: number;
  cashAndShortTermInvestments: number;
  netReceivables: number;
  inventory: number;
  otherCurrentAssets: number;
  totalCurrentAssets: number;
  propertyPlantEquipmentNet: number;
  goodwill: number;
  intangibleAssets: number;
  goodwillAndIntangibleAssets: number;
  longTermInvestments: number;
  taxAssets: number;
  otherNonCurrentAssets: number;
  totalNonCurrentAssets: number;
  otherAssets: number;
  totalAssets: number;
  accountPayables: number;
  shortTermDebt: number;
  taxPayables: number;
  deferredRevenue: number;
  otherCurrentLiabilities: number;
  totalCurrentLiabilities: number;
  longTermDebt: number;
  deferredRevenueNonCurrent: number;
  deferredTaxLiabilitiesNonCurrent: number;
  otherNonCurrentLiabilities: number;
  totalNonCurrentLiabilities: number;
  otherLiabilities: number;
  capitalLeaseObligations: number;
  totalLiabilities: number;
  preferredStock: number;
  commonStock: number;
  retainedEarnings: number;
  accumulatedOtherComprehensiveIncomeLoss: number;
  othertotalStockholdersEquity: number;
  totalStockholdersEquity: number;
  totalEquity: number;
  totalLiabilitiesAndStockholdersEquity: number;
  minorityInterest: number;
  totalLiabilitiesAndTotalEquity: number;
  totalInvestments: number;
  totalDebt: number;
  netDebt: number;
  link: string;
  finalLink: string;
}

export function useCompanyBalanceSheet(
  ticker: string,
  period: 'quarter' | 'annual' = 'quarter',
  limit?: number
) {
  return useQuery<BalanceSheetStatement[], Error>({
    queryKey: ["companyBalanceSheet", ticker, period, limit],
    queryFn: async () => {
      const params: any = { ticker, period };
      if (limit) {
        params.limit = limit.toString();
      }

      const { data } = await api.get<BalanceSheetStatement[]>(
        "/company-balance-sheet",
        {
          params,
        }
      );
      return data;
    },
    enabled: !!ticker,
    refetchOnWindowFocus: false,
    staleTime: 2.5 * 60 * 1000, // 2.5 minutes for fresh data
    retry: (failureCount, error) => {
      // Don't retry on 404s - legitimately means no balance sheet data exists
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((error as any)?.response?.status === 404) {
        return false;
      }
      // Retry other errors (500s, network issues) max 2 times
      return failureCount < 2;
    },
  });
}
